import { ApiRouteHelpers as Helpers } from "../../../root/apiRoutes";
import {
  RamsayChatMessageRequest,
  RamsayChatMessageResponse,
  RamsayChatStreamDeltaDto,
  RamsayChatStreamErrorDto,
} from "../dto/RamsayDto";
import { OrganizationPublicId } from "../Types";

interface SseEvent {
  event: string;
  data: string;
}

// The server terminates each SSE event with a blank line. Data may span multiple `data:` lines.
const parseSseEvent = (rawEvent: string): SseEvent => {
  let event = "message";
  const dataLines: string[] = [];
  for (const line of rawEvent.split("\n")) {
    if (line.startsWith("event:")) {
      event = line.slice("event:".length).trim();
    } else if (line.startsWith("data:")) {
      dataLines.push(line.slice("data:".length).replace(/^ /, ""));
    }
  }
  return { event, data: dataLines.join("\n") };
};

// The Ramsay message endpoint streams Server-Sent Events, which the shared axios instance
// cannot consume (its response interceptor rejects non-JSON /api bodies), so this controller
// uses fetch with a minimal SSE parser instead.
export class RamsayController {
  // Fire-and-forget: starts the conversation's backing VM booting so the first reply is less
  // likely to hit a cold start. Errors are irrelevant to the caller and swallowed.
  public warm = (organizationId: OrganizationPublicId, conversationId: string): void => {
    const url = Helpers.organization.ramsay.warm(organizationId);
    void fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ conversationId }),
    }).catch(() => undefined);
  };

  public streamMessage = async (
    organizationId: OrganizationPublicId,
    request: RamsayChatMessageRequest,
    onDelta: (text: string) => void,
  ): Promise<RamsayChatMessageResponse> => {
    const url = Helpers.organization.ramsay.messages(organizationId);
    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
      credentials: "include",
      body: JSON.stringify(request),
    });
    if (!response.ok || !response.body) {
      throw new Error(`Ramsay stream failed with status ${response.status}`);
    }

    const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
    let buffer = "";
    let done: RamsayChatMessageResponse | undefined;

    const handleEvent = (rawEvent: string) => {
      const { event, data } = parseSseEvent(rawEvent);
      if (event === "delta") {
        onDelta((JSON.parse(data) as RamsayChatStreamDeltaDto).text);
      } else if (event === "done") {
        done = JSON.parse(data) as RamsayChatMessageResponse;
      } else if (event === "error") {
        throw new Error((JSON.parse(data) as RamsayChatStreamErrorDto).message);
      }
    };

    try {
      for (;;) {
        const { value, done: streamEnded } = await reader.read();
        if (streamEnded) {
          break;
        }
        buffer += value.replace(/\r\n/g, "\n");
        let separatorIndex = buffer.indexOf("\n\n");
        while (separatorIndex !== -1) {
          const rawEvent = buffer.slice(0, separatorIndex);
          buffer = buffer.slice(separatorIndex + 2);
          handleEvent(rawEvent);
          separatorIndex = buffer.indexOf("\n\n");
        }
      }
    } catch (error) {
      void reader.cancel().catch(() => undefined);
      throw error;
    }

    if (!done) {
      throw new Error("Ramsay stream ended without a done event");
    }
    return done;
  };
}
